import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
  DestroyRef,
  signal,
  OnInit,
  effect,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { catchError, of } from "rxjs";

import { TeacherProfile } from "../../../api/models/teacher-profile";
import { School } from "../../../api/models/school";
import { SchoolClass } from "../../../api/models/school-class";
import { TeacherProfileService } from "../../../api/services/teacher-profile.service";
import { SchoolService } from "../../../api/services/school.service";
import {
  AlertService,
  alertMessages,
} from "../../../shared/services/alert.service";

@Component({
  selector: "app-teacher-profile-tab",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslateModule, FontAwesomeModule],
  template: `
    <div>
      <div class="card mb-3">
        <div class="card-body">
          <h5 class="card-title">{{ "Personal information" | translate }}</h5>
          <div class="form">
            <div class="form-group">
              <label>{{ "First name" | translate }}</label>
              <input
                type="text"
                class="form-control"
                [ngModel]="profile().firstName"
                (ngModelChange)="updateProfileField('firstName', $event)"
              />
            </div>
            <div class="form-group">
              <label>{{ "Last name" | translate }}</label>
              <input
                type="text"
                class="form-control"
                [ngModel]="profile().lastName"
                (ngModelChange)="updateProfileField('lastName', $event)"
              />
            </div>
            <div class="form-group">
              <label>{{ "Phone number" | translate }}</label>
              <input
                type="text"
                class="form-control"
                [ngModel]="profile().phoneNumber"
                (ngModelChange)="updateProfileField('phoneNumber', $event)"
              />
            </div>
            <div class="btn-toolbar">
              <button
                class="btn btn-primary ml-auto"
                (click)="updateTeacherProfile()"
              >
                <fa-icon [icon]="faSave"></fa-icon> {{ "Save" | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>

      @if (profile().address) {
      <div class="card mb-3">
        <div class="card-body">
          <h5 class="card-title">{{ "Address" | translate }}</h5>
          <div class="form">
            <div class="form-group">
              <label>{{ "Street" | translate }}</label>
              <input
                type="text"
                class="form-control"
                [ngModel]="profile().address.street"
                (ngModelChange)="updateAddressField('street', $event)"
              />
            </div>
            <div class="row">
              <div class="col-8">
                <div class="form-group">
                  <label>{{ "Number" | translate }}</label>
                  <input
                    type="text"
                    class="form-control"
                    [ngModel]="profile().address.number"
                    (ngModelChange)="updateAddressField('number', $event)"
                  />
                </div>
              </div>
              <div class="col-4">
                <div class="form-group">
                  <label>{{ "Suffix" | translate }}</label>
                  <input
                    type="text"
                    class="form-control"
                    [ngModel]="profile().address.numberSuffix"
                    (ngModelChange)="updateAddressField('numberSuffix', $event)"
                  />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-4">
                <div class="form-group">
                  <label>{{ "Postal code" | translate }}</label>
                  <input
                    type="text"
                    class="form-control"
                    [ngModel]="profile().address.postalCode"
                    (ngModelChange)="updateAddressField('postalCode', $event)"
                  />
                </div>
              </div>
              <div class="col-8">
                <div class="form-group">
                  <label>{{ "City" | translate }}</label>
                  <input
                    type="text"
                    class="form-control"
                    [ngModel]="profile().address.city"
                    (ngModelChange)="updateAddressField('city', $event)"
                  />
                </div>
              </div>
            </div>
            <div class="form-group">
              <label>{{ "Country" | translate }}</label>
              <input
                type="text"
                class="form-control"
                [ngModel]="profile().address.country"
                (ngModelChange)="updateAddressField('country', $event)"
              />
            </div>
            <div class="btn-toolbar">
              <button
                class="btn btn-primary ml-auto"
                (click)="updateTeacherProfile()"
              >
                <fa-icon [icon]="faSave"></fa-icon> {{ "Save" | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>
      }

      <div class="card mb-3">
        <div class="card-body">
          <h5 class="card-title">{{ "School" | translate }}</h5>

          <div class="form-group">
            <label>{{ "Select a school" | translate }}</label>
            <select
              class="form-control"
              [ngModel]="selectedSchoolId()"
              (ngModelChange)="onSchoolChange($event)"
            >
              <option value="">{{ "Select a school" | translate }}</option>
              @for (school of schools(); track school.id) {
              <option [value]="school.id">{{ school.name }}</option>
              }
            </select>
          </div>

          @if (availableClasses().length > 0) {
          <div class="form-group">
            <label>{{ "Select classes" | translate }}</label>
            @for (cls of availableClasses(); track cls.id) {
            <div class="custom-control custom-checkbox">
              <input
                [id]="'class-' + cls.id"
                class="custom-control-input"
                type="checkbox"
                [checked]="isClassSelected(cls.id!)"
                (change)="toggleClass(cls.id!)"
              />
              <label [for]="'class-' + cls.id" class="custom-control-label">{{
                cls.name
              }}</label>
            </div>
            }
          </div>
          }

          <div class="btn-toolbar">
            <button
              class="btn btn-primary ml-auto"
              (click)="saveSchoolClasses()"
            >
              <fa-icon [icon]="faSave"></fa-icon> {{ "Save" | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class TeacherProfileTabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly teacherProfileService = inject(TeacherProfileService);
  private readonly schoolService = inject(SchoolService);
  private readonly alertService = inject(AlertService);

  profile = input.required<TeacherProfile>();
  schoolClasses = input.required<SchoolClass[]>();
  profileChanged = output<void>();

  readonly localProfile = signal<TeacherProfile | null>(null);
  readonly schools = signal<School[]>([]);
  readonly availableClasses = signal<SchoolClass[]>([]);
  readonly selectedSchoolId = signal<string>("");
  readonly selectedClassIds = signal<string[]>([]);

  faSave = faSave;

  private initialized = false;

  constructor() {
    // Sync current school/classes when profile input changes
    effect(() => {
      const profile = this.profile();
      const currentClasses = this.schoolClasses();
      if (!this.initialized && profile) {
        this.initialized = true;
        const currentSchoolId = profile.schools?.[0]?.id || "";
        this.selectedSchoolId.set(currentSchoolId);
        this.selectedClassIds.set(currentClasses.map((c) => c.id!));
        if (currentSchoolId) {
          this.loadAvailableClasses(currentSchoolId);
        }
      }
    });
  }

  ngOnInit(): void {
    this.loadSchools();
  }

  loadSchools(): void {
    this.schoolService
      .getSchools({ PageSize: 500 })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result: any) => {
        if (result?.data) {
          this.schools.set(result.data);
        }
      });
  }

  loadAvailableClasses(schoolId: string): void {
    this.schoolService
      .getSchoolClasses({ schoolId })
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.availableClasses.set(result);
      });
  }

  onSchoolChange(schoolId: string): void {
    this.selectedSchoolId.set(schoolId);
    this.selectedClassIds.set([]);
    this.availableClasses.set([]);

    if (schoolId) {
      this.loadAvailableClasses(schoolId);
    }
  }

  toggleClass(classId: string): void {
    this.selectedClassIds.update((ids) => {
      const index = ids.indexOf(classId);
      if (index > -1) {
        return ids.filter((id) => id !== classId);
      }
      return [...ids, classId];
    });
  }

  isClassSelected(classId: string): boolean {
    return this.selectedClassIds().includes(classId);
  }

  saveSchoolClasses(): void {
    const profileId = this.profile().id;
    if (!profileId) return;

    const selectedClasses = this.availableClasses()
      .filter((cls) => this.selectedClassIds().includes(cls.id!))
      .map((cls) => ({ id: cls.id, name: cls.name } as SchoolClass));

    this.teacherProfileService
      .setTeacherProfileSchoolClasses({
        profileId,
        body: selectedClasses,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.alertService.success(alertMessages.successDataSaved);
          this.profileChanged.emit();
        },
      });
  }

  updateProfileField(field: string, value: string) {
    const current = this.profile();
    if (!current) return;
    this.localProfile.set({ ...current, [field]: value });
  }

  updateAddressField(field: string, value: string) {
    const current = this.localProfile() || this.profile();
    if (!current) return;
    this.localProfile.set({
      ...current,
      address: { ...current.address, [field]: value },
    });
  }

  updateTeacherProfile() {
    const profile = this.localProfile() || this.profile();
    if (!profile?.id) return;

    this.teacherProfileService
      .updateTeacherProfile({ id: profile.id, body: profile })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.profileChanged.emit() });
  }
}
